/**
 * PersistentControls Component
 *
 * Container component that renders persistent widget controls:
 * - Mandatory filters (always visible)
 *
 * This bar appears between the widget header and content area.
 */

import React from 'react';
import { MandatoryFilters } from './MandatoryFilters';
import type { QueryFilter, WidgetFilterConfig } from '../../types/analytics';

export interface PersistentControlsProps {
  // Mandatory filter props
  /** Current filter values */
  filters: QueryFilter[];
  /** Mandatory filter configurations (filters with required: true) */
  mandatoryFilterConfigs: WidgetFilterConfig[];
  /** Callback when filters change */
  onFiltersChange: (filters: QueryFilter[]) => void;

  // Common props
  /** Whether controls are disabled (e.g., read-only mode) */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * PersistentControls - Toolbar with mandatory filters
 *
 * Only renders if there are mandatory filters to show.
 */
export const PersistentControls: React.FC<PersistentControlsProps> = ({
  filters,
  mandatoryFilterConfigs,
  onFiltersChange,
  disabled = false,
  className = '',
}) => {
  const showMandatoryFilters = mandatoryFilterConfigs && mandatoryFilterConfigs.length > 0;

  if (!showMandatoryFilters) {
    return null;
  }

  return (
    <div
      className={`
        persistent-controls
        flex items-center gap-4 px-3 py-2
        bg-gray-50 border-b border-gray-100
        ${className}
      `}
    >
      <MandatoryFilters
        filters={filters}
        filterConfigs={mandatoryFilterConfigs}
        onFiltersChange={onFiltersChange}
        disabled={disabled}
      />
    </div>
  );
};

export default PersistentControls;
